import * as z from 'zod'

import { UpdateArticleVars } from '@/common/api/params/articles'
import { NameLabelMapping } from '@/common/app/message'
import {
  ARTICLE_SLUG_EXT_STEM_EDITABLE,
  ArticleSlugKind,
  articleSlugKind
} from '@/common/app/modules/media/article'
import { StringLength } from '@/common/form/schema'
import {
  ARTICLE_PERSONA_MAX_LENGTH,
  ArticlePartRecord,
  ArticleProposalDetail,
  ArticleStatus,
  ArticleTone
} from '@/common/model/media/article'

export const articleEditSchema = z
  .object({
    title: z.string().max(StringLength.NORMAL_MAX),
    // .liquid のスラッグは、拡張子を除いたファイル名を入力値にする（保存時に拡張子を付ける）
    slug: z.string().max(StringLength.NORMAL_MAX),
    // スラッグの拡張子から決めた編集できる範囲と、編集前のスラッグ（.aspx は入力欄を編集不可にして元の値を送る）
    slugKind: z.custom<ArticleSlugKind>(),
    slugOriginal: z.string(),
    keyword: z.string().max(StringLength.TEXT_MAX),
    headingOutline: z.string().max(StringLength.TEXT_MAX),
    tone: z.custom<ArticleTone>(),
    persona: z.string().max(ARTICLE_PERSONA_MAX_LENGTH),
    targetCharCount: z.number().min(1000).max(5000),
    metaDescription: z.string().max(StringLength.TEXT_MAX),
    partPageTitle: z.string().min(1, 'ページタイトルは必須です').max(StringLength.NORMAL_MAX),
    headerBannerImagePath: z.string(),
    contentAreaTop: z.string().max(StringLength.HTML_MAX),
    contentAreaBottom: z.string().max(StringLength.HTML_MAX),
    status: z.string(),
    // AI（AIリライト）を使用したか。保存時に articles.ai_used へ記録する
    aiUsed: z.boolean()
  })
  .superRefine((data, ctx) => {
    if (data.slugKind === ArticleSlugKind.STEM_EDITABLE && !data.slug.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['slug'],
        message: `ファイル名を入力してください（拡張子 ${ARTICLE_SLUG_EXT_STEM_EDITABLE} は変更できません）`
      })
    }
  })

export type ArticleEditSchemaType = z.infer<typeof articleEditSchema>

const findPart = (parts: ArticlePartRecord[], partType: string): ArticlePartRecord | undefined =>
  parts.find((p) => p.part_type === partType)

// スラッグ → 入力欄の値（.liquid は拡張子を除いたファイル名）
const slugInputValue = (slug: string, kind: ArticleSlugKind): string =>
  kind === ArticleSlugKind.STEM_EDITABLE
    ? slug.slice(0, -ARTICLE_SLUG_EXT_STEM_EDITABLE.length)
    : slug

// 入力欄の値 → 保存するスラッグ
const slugToSave = (data: ArticleEditSchemaType): string | null => {
  if (data.slugKind === ArticleSlugKind.FIXED) return data.slugOriginal
  if (data.slugKind === ArticleSlugKind.STEM_EDITABLE) {
    // 拡張子は編集前のスラッグのものを残す（大文字・小文字も変えない）
    return data.slug.trim() + data.slugOriginal.slice(-ARTICLE_SLUG_EXT_STEM_EDITABLE.length)
  }
  return data.slug.trim() || null
}

// APIレスポンス → フォーム値
export const convertDetailToSchema = (detail: ArticleProposalDetail): ArticleEditSchemaType => {
  const slugOriginal = detail.latest_article.slug ?? ''
  const slugKind = articleSlugKind(slugOriginal)
  return {
    title: detail.latest_article.title ?? '',
    slug: slugInputValue(slugOriginal, slugKind),
    slugKind,
    slugOriginal,
    keyword: detail.latest_article.keyword ?? '',
    headingOutline: detail.latest_article.heading_outline ?? '',
    tone: detail.latest_article.tone ?? 'professional',
    persona: detail.latest_article.persona ?? '',
    targetCharCount: detail.latest_article.target_char_count ?? 3000,
    metaDescription: detail.latest_article.meta_description ?? '',
    partPageTitle: findPart(detail.parts, 'page_title')?.page_title ?? '',
    headerBannerImagePath: findPart(detail.parts, 'header_banner_image')?.image_path ?? '',
    contentAreaTop: findPart(detail.parts, 'content_area_top')?.content_html ?? '',
    contentAreaBottom: findPart(detail.parts, 'content_area_bottom')?.content_html ?? '',
    status: detail.latest_article.status,
    // 既存バージョンの記録を引き継ぐ（AIリライト適用時に true へ更新）
    aiUsed: detail.latest_article.ai_used
  }
}

// フォーム値 → 更新パラメータ（商品一覧は編集対象外のため既存の内容をそのまま引き継ぐ）
export const convertSchemaToUpdateVars = (
  siteId: number,
  articleId: number,
  data: ArticleEditSchemaType,
  productListPart: ArticlePartRecord | undefined
): UpdateArticleVars => ({
  site_id: siteId,
  article_id: articleId,
  title: data.title.trim(),
  slug: slugToSave(data),
  keyword: data.keyword.trim(),
  heading_outline: data.headingOutline.trim() || null,
  tone: data.tone as ArticleTone,
  persona: data.persona.trim() || null,
  target_char_count: data.targetCharCount,
  meta_description: data.metaDescription.trim() || null,
  status: data.status as ArticleStatus,
  ai_used: data.aiUsed,
  parts: [
    { part_type: 'page_title', page_title: data.partPageTitle },
    { part_type: 'header_banner_image', image_path: data.headerBannerImagePath || null },
    { part_type: 'content_area_top', content_html: data.contentAreaTop },
    {
      part_type: 'product_list',
      content_html: productListPart?.content_html ?? null,
      image_path: productListPart?.image_path ?? null
    },
    { part_type: 'content_area_bottom', content_html: data.contentAreaBottom }
  ]
})

export const articleEditNameLabelMapping: NameLabelMapping = {
  pageTitle: 'ページタイトル'
}
